import React, { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import './App.css';

function App() {
  // State for user selections
  const [book, setBook] = useState(1);
  const [chapter, setChapter] = useState(1);
  const [character, setCharacter] = useState('Carl');
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [characterData, setCharacterData] = useState({});
  const [maxChapters, setMaxChapters] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Book data
  const books = useMemo(() => [
    { id: 1, title: "Dungeon Crawler Carl - Book 1" },
    { id: 2, title: "Carl's Doomsday Scenario - Book 2" },
    { id: 3, title: "The Dungeon Anarchist's Cookbook - Book 3" },
    { id: 4, title: "The Gate of the Feral Gods - Book 4" },
    { id: 5, title: "The Butcher's Masquerade - Book 5" },
    { id: 6, title: "The Eye of the Bedlam Bride - Book 6" },
    { id: 7, title: "This Inevitable Ruin - Book 7" }
  ], []);

  // Characters
  const characters = [
    { id: 'Carl', name: 'Carl' },
    { id: 'Donut', name: 'Princess Donut' },
    { id: 'Katia', name: 'Katia' },
  ];

  // Fetch inventory data from JSON files
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Load the JSON files from the public folder
        const carlItemsResponse = await fetch('/dcc_carl_items.json');
        const donutItemsResponse = await fetch('/dcc_donut_items.json');
        const katiaItemsResponse = await fetch('/dcc_katia_items.json');
        
        // Parse JSON data
        const carlItems = await carlItemsResponse.json();
        const donutItems = await donutItemsResponse.json();
        const katiaItems = await katiaItemsResponse.json();
        
        // Calculate max chapters per book
        const chaptersMap = {};
        const allItems = [...carlItems, ...donutItems, ...katiaItems];
        
        allItems.forEach(item => {
          if (item.book !== null && item.chapter !== null) {
            if (!chaptersMap[item.book] || item.chapter > chaptersMap[item.book]) {
              chaptersMap[item.book] = item.chapter;
            }
          }
        });
        
        // Ensure all books have at least some chapters
        books.forEach(book => {
          if (!chaptersMap[book.id]) {
            chaptersMap[book.id] = 30; // Default chapter count if none found
          }
        });
        
        setMaxChapters(chaptersMap);
        
        // Set character data
        setCharacterData({
          'Carl': carlItems,
          'Donut': donutItems,
          'Katia': katiaItems
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading inventory data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [books]);

  // Update inventory whenever selections change
  useEffect(() => {
    if (!loading && characterData[character]) {
      // Filter items based on book and chapter
      const filteredItems = characterData[character].filter(item => {
        // Include items that have null book/chapter (they're always available)
        if (item.book === null || item.chapter === null) return true;
        
        // Include items from earlier books
        if (item.book < book) return true;
        
        // Include items from current book up to current chapter
        if (item.book === book && item.chapter <= chapter) return true;
        
        return false;
      });
      
      setInventory(filteredItems);
    }
  }, [book, chapter, character, loading, characterData]);

  // Generate chapter options based on selected book
  const generateChapterOptions = () => {
    const max = maxChapters[book] || 30; // Default to 30 chapters if data not available
    return Array.from({ length: max }, (_, i) => i + 1);
  };

  // Group items by type for display
  const groupedInventory = inventory.length > 0 ? _.groupBy(inventory, 'type') : {};

  // Format item type for display
  const formatItemType = (type) => {
    if (!type) return "Miscellaneous";
    
    // Remove any text in parentheses
    const cleanType = type.replace(/\([^)]*\)/g, '').trim();
    
    // Split on commas and take first part
    return cleanType.split(',')[0];
  };

  return (
    <div className="p-6 max-w-4xl mx-auto bg-gray-50 rounded-lg shadow-md">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Dungeon Crawler Carl Inventory Tracker</h1>
      
      {loading ? (
        <p className="text-lg text-center">Loading inventory data...</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {/* Book Selection */}
            <div className="bg-white p-4 rounded shadow">
              <label className="block text-sm font-medium text-gray-700 mb-2">Book</label>
              <select 
                className="w-full p-2 border border-gray-300 rounded"
                value={book}
                onChange={(e) => {
                  setBook(parseInt(e.target.value));
                  setChapter(1); // Reset chapter when book changes
                }}
              >
                {books.map(b => (
                  <option key={b.id} value={b.id}>{b.title}</option>
                ))}
              </select>
            </div>

            {/* Chapter Selection */}
            <div className="bg-white p-4 rounded shadow">
              <label className="block text-sm font-medium text-gray-700 mb-2">Chapter</label>
              <select 
                className="w-full p-2 border border-gray-300 rounded"
                value={chapter}
                onChange={(e) => setChapter(parseInt(e.target.value))}
              >
                {generateChapterOptions().map(ch => (
                  <option key={ch} value={ch}>Chapter {ch}</option>
                ))}
              </select>
            </div>

            {/* Character Selection */}
            <div className="bg-white p-4 rounded shadow">
              <label className="block text-sm font-medium text-gray-700 mb-2">Character</label>
              <select 
                className="w-full p-2 border border-gray-300 rounded"
                value={character}
                onChange={(e) => setCharacter(e.target.value)}
              >
                {characters.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Character Inventory Display */}
          <div className="bg-white p-6 rounded shadow">
            <h2 className="text-2xl font-semibold mb-4">
              {characters.find(c => c.id === character)?.name}'s Inventory
              <span className="text-sm ml-2 text-gray-500">
                (Book {book}, Chapter {chapter})
              </span>
            </h2>

            {inventory.length === 0 ? (
              <p className="text-gray-600 italic">No items acquired yet at this point in the story.</p>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedInventory).length > 0 ? (
                  Object.entries(groupedInventory).map(([type, items]) => (
                    <div key={type} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="text-lg font-medium mb-3 text-gray-700 bg-gray-100 p-2 rounded">
                        {formatItemType(type)}
                      </h3>
                      <div className="space-y-4">
                        {items.map(item => (
                          <div 
                            key={item.name} 
                            className="border-t border-gray-100 pt-2 cursor-pointer hover:bg-gray-50 transition-colors rounded p-2"
                            onClick={() => {
                              setSelectedItem(item);
                              setShowModal(true);
                            }}
                          >
                            <div className="flex justify-between">
                              <div className="text-lg font-medium text-gray-800">{item.name}</div>
                              <div className="text-gray-500 text-sm">
                                {item.book && item.chapter ? `Book ${item.book}, Ch ${item.chapter}` : 'Always Available'}
                              </div>
                            </div>
                            <div className="mt-2 text-gray-600 whitespace-pre-line">{item.effects}</div>
                            <div className="text-blue-500 text-sm mt-1 italic">Click to see full description</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-600 italic">Error grouping items. Please try again.</p>
                )}
              </div>
            )}
          </div>

          {/* Modal for displaying item descriptions */}
          {showModal && selectedItem && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
              onClick={() => setShowModal(false)}
            >
              <div 
                className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-auto shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white z-10 border-b">
                  <div className="flex justify-between items-center p-4">
                    <h3 className="text-2xl font-bold text-gray-800">{selectedItem.name}</h3>
                    <button 
                      onClick={() => setShowModal(false)}
                      className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                    >
                      &times;
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="mb-4">
                    <span className="font-semibold">Type:</span> {selectedItem.type || 'N/A'}
                  </div>
                  
                  <div className="mb-4">
                    <span className="font-semibold">Acquired:</span> {selectedItem.book && selectedItem.chapter 
                      ? `Book ${selectedItem.book} (${books.find(b => b.id === selectedItem.book)?.title}), Chapter ${selectedItem.chapter}` 
                      : 'Always Available'}
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-lg mb-2">Effects:</h4>
                    <div className="bg-gray-50 p-3 rounded whitespace-pre-line">
                      {selectedItem.effects}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-lg mb-2">Description:</h4>
                    <div className="bg-gray-50 p-3 rounded whitespace-pre-line">
                      {selectedItem.aiDescription}
                    </div>
                  </div>
                </div>
                
                <div className="sticky bottom-0 bg-gray-100 px-6 py-4 border-t">
                  <button 
                    onClick={() => setShowModal(false)}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default App;
